import React, { useEffect, useState, useRef, useLayoutEffect, useCallback } from "react";
import { useSelector, useDispatch, useStore, batch } from "react-redux";
import { Box, Collapse, Drawer, IconButton, useMediaQuery, useTheme } from "@mui/material";
import AuthView from "../../utils/AuthView";
import Grid from "@mui/material/Grid2";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
// import LeftNav from "../../components/Navbar/LeftNav";
import InboxSettings from "../../components/Inbox/InboxSettings";
import InboxAll from "../../components/Inbox/InboxAll";
import ChatBox from "../../components/Inbox/ChatBox";
import ChatboxNavbar from "../../components/Inbox/Features/ChatboxNavbar";
import ChatInfo from "../../components/Inbox/ChatInfo";
import GroupInfoDrawer from "../../components/Inbox/GroupInfoDrawer";
import ChatInfoRail from "../../components/Inbox/ChatInfoRail";
import {
  setInboxSettings,
  setChatInfo,
  setScreenSize,
} from "../../redux/Slice/Inbox/inboxSlice";
import { useNavigate, useParams } from "react-router-dom";
import { fetchInbox } from "../../redux/Slice/Inbox/inboxRecentSlice";
import { setselectedContactID } from "../../redux/Slice/Inbox/inboxAllSlice";
import { clearMessages, setExpectedContact } from "../../redux/Slice/Inbox/InboxMessageSlice";
import { clearContactInfo } from "../../redux/Slice/Inbox/inboxInfoSlice";
import { clearCurrentChat } from "../../redux/Slice/Inbox/teamMemberChatSlice";
import {
  selectSelectedContactID,
  selectSelectedInboxTab,
  selectUserDetailsData,
  selectInboxRecentLoading,
  selectInboxRecentError,
  selectInboxRecentInitialized,
  selectInboxMessageLoading,
  selectContactExistsInInbox,
  selectMessages,
} from "../../redux/selectors";

// Calculate available height for each screen size
// TopNav: xs=48px, sm=56px, md/lg/xl=64px
// BottomNav: xs=55px, sm=60px, md/lg/xl=0px
// Bottom margin: 0px for all (removed to eliminate gap)
const getAvailableHeight = () => {
  // Home.jsx flexbox already constrains the Outlet to the remaining space after
  // TopNav and TrialBanner, so all children must use 100% — not 100vh — to avoid
  // overflowing when the banner is visible.
  return "100%";
};

function Inbox() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accountid, userid } = useParams();
  // useEffect(() => {
  //   dispatch(setLoading(true));

  //   // Simulate API call or heavy operation
  //   const timeout = setTimeout(() => {
  //     dispatch(setLoading(false));
  //   }, 1000);

  //   return () => clearTimeout(timeout);
  // }, [dispatch]);
  const { showInboxSettings, screenSize, showChatInfo } = useSelector(
    (state) => state.inbox
  );

  const theme = useTheme();
  // Proper breakpoint detection
  const isXs = useMediaQuery(theme.breakpoints.down("sm")); // 0-599px
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md")); // 600-899px
  const isMd = useMediaQuery(theme.breakpoints.between("md", "lg")); // 900-1199px
  const isLg = useMediaQuery(theme.breakpoints.between("lg", "xl")); // 1200-1535px
  const isXl = useMediaQuery(theme.breakpoints.up("xl")); // 1536px+

  const ConversationPermissions = useSelector(
    (state) => state.userDetails.userPermissions?.permissions?.Conversation || {}
  );

  const selectedContactID = useSelector(selectSelectedContactID);
  const store = useStore();
  const selectedInboxTab = useSelector(selectSelectedInboxTab);
  const userDetails = useSelector(selectUserDetailsData);
  const inboxRecentLoading = useSelector(selectInboxRecentLoading);
  const inboxRecentError = useSelector(selectInboxRecentError);
  const inboxRecentInitialized = useSelector(selectInboxRecentInitialized);
  const inboxMessageLoading = useSelector(selectInboxMessageLoading);
  const inboxExpectedContactId = useSelector((state) => state.inboxMessage.expectedContactId);
  const messages = useSelector(selectMessages);
  const contactExistsInInbox = useSelector(selectContactExistsInInbox);
  
  // Check if team member chat is active (to hide ChatInfo)
  const currentTeamMemberChat = useSelector((state) => state.teamMemberChat?.currentChat);
  const isTeamMemberChat = !!(currentTeamMemberChat?.otherMemberId || currentTeamMemberChat?.otherMember?.id);
  // Same formula ChatBox.jsx uses for its own ChatboxNavbar's isTeamMemberChat prop —
  // needed here too now that XL renders ChatboxNavbar itself (see canShowChatInfoPanel).
  const isChatWithMembersTab = selectedInboxTab === "Chat with Members";
  // WhatsApp Groups plan follow-up (2026-09-26): swaps ChatInfo.jsx (a 1:1-contact-specific
  // component, contactInfo is always null for a group) for GroupInfoDrawer.jsx at the SM/MD/XS
  // Drawer mounts below -- the same swap ChatInfoRail.jsx/ChatboxNavbar.jsx already make for
  // the LG/XL persistent rail. `messages` (selectMessages, already selected above) already
  // carries isGroup once a group thread is open, so this needs no new fetch/selector.
  const isGroup = !!messages?.isGroup;

  const [openDialog, setOpenDialog] = useState(null); // 'assign', 'close', 'template', 'label', null
  const [showInboxSettingsDrawer, setShowInboxSettingsDrawer] = useState(false);
  const [showChatInfoDrawer, setShowChatInfoDrawer] = useState(false);
  const [showChatView, setShowChatView] = useState(false); // For XS: controls whether to show chat or recents
  const prevSelectedContactIDRef = useRef(selectedContactID); // Track previous contact ID to detect new selections
  const prevIsXsRef = useRef(isXs); // Track previous XS state to detect resize transitions
  const chatInfoManuallyClosedRef = useRef(false); // Track if user manually closed ChatInfo on XL
  const hasInboxFetchStartedRef = useRef(false);
  const hasInboxFetchCompletedRef = useRef(false);
  useEffect(() => {
    if (inboxRecentLoading) {
      hasInboxFetchStartedRef.current = true;
    }
    if (hasInboxFetchStartedRef.current && !inboxRecentLoading) {
      hasInboxFetchCompletedRef.current = true;
    }
  }, [inboxRecentLoading]);

  // Which breakpoint bucket we're in, as a single value — lets the effect below tell
  // "the breakpoint actually changed" apart from "some unrelated dep (selectedContactID
  // etc) changed and re-ran this effect" (see screenCategoryRef just below).
  const screenCategory = isXs ? "xs" : isSm ? "sm" : isMd ? "md" : isLg ? "lg" : isXl ? "xl" : null;
  const screenCategoryRef = useRef(null);

  // Update screen size and default states based on breakpoints
  useEffect(() => {
    // BUG FIX (2026-09-01, Rahim: "close the left panel and click any chat, it reopens
    // itself"): this effect's dependency array includes selectedContactID (needed by the
    // XS resize-clear-contact logic below), so it re-runs on every chat selection, not
    // just on real breakpoint changes. The isLg/isXl branches used to unconditionally
    // dispatch setInboxSettings(true) every time this fired, silently undoing the user's
    // manual close of the InboxSettings (left) panel the moment they picked a new chat.
    // Gate the per-category default-state dispatches on the breakpoint bucket actually
    // having changed; the XS contact-clearing block further down already has its own
    // transition guard (`wasLarger`) and is untouched.
    const categoryChanged = screenCategory !== screenCategoryRef.current;
    screenCategoryRef.current = screenCategory;

    if (isXs) {
      const wasLarger = !prevIsXsRef.current;
      prevIsXsRef.current = true;
      if (categoryChanged) {
        batch(() => {
          dispatch(setScreenSize("xs"));
          dispatch(setInboxSettings(false));
          dispatch(setChatInfo(false));
        });
      }
      // When resizing DOWN into XS: clear the selected contact + URL.
      // Without this, selectedContactID stays set but the chat panel is hidden,
      // making re-clicks on the same contact a no-op (useContactSelection sees it as cached).
      if (wasLarger && selectedContactID) {
        batch(() => {
          dispatch(setselectedContactID(null));
          dispatch(setExpectedContact(null));
          dispatch(clearMessages());
          dispatch(clearContactInfo());
        });
        const effectiveUserid = userid || userDetails?._id;
        if (accountid && effectiveUserid) {
          navigate(`/home/${accountid}/inbox/${effectiveUserid}`, { replace: true });
        }
      }
      // NOT unconditional (was: a flat `setShowChatView(false)` here, every time
      // this effect fires for ANY reason while isXs is true). This effect's own
      // dependency array is broad (dispatch/userid/userDetails/accountid/navigate,
      // not just isXs+selectedContactID), so it re-runs for reasons that have
      // nothing to do with the chat selection -- e.g. userDetails finishing a
      // fetch shortly after a contact is tapped. An unconditional false here was
      // clobbering the dedicated "Handle chat view on XS" effect below the very
      // next time THIS effect re-fired for an unrelated reason, snapping the
      // user straight back to the recents list right after opening a chat --
      // "for phone view... no way [to open messages]" (Rahim, 2026-09-10). Only
      // reset to the list here when there's genuinely no chat to show; the
      // dedicated effect below is the real, narrowly-scoped source of truth for
      // showChatView otherwise.
      if (!selectedContactID && !isTeamMemberChat) {
        setShowChatView(false);
      }
    } else if (isSm) {
      prevIsXsRef.current = false;
      if (categoryChanged) {
        batch(() => {
          dispatch(setScreenSize("sm"));
          dispatch(setInboxSettings(false));
          dispatch(setChatInfo(false));
        });
      }
    } else if (isMd) {
      prevIsXsRef.current = false;
      if (categoryChanged) {
        batch(() => {
          dispatch(setScreenSize("md"));
          dispatch(setInboxSettings(false));
          dispatch(setChatInfo(false));
        });
      }
    } else if (isLg) {
      prevIsXsRef.current = false;
      if (categoryChanged) {
        batch(() => {
          dispatch(setScreenSize("lg"));
          dispatch(setInboxSettings(true)); // Show InboxSettings on LG
          // showChatInfo no longer drives anything on LG (2026-08-27) — the rail is
          // persistent like XL's, gated by canShowChatInfoPanel instead. Dispatched
          // false here only because other screenSize branches also reset it; harmless.
          dispatch(setChatInfo(false));
        });
      }
    } else if (isXl) {
      prevIsXsRef.current = false;
      if (categoryChanged) {
        batch(() => {
          dispatch(setScreenSize("xl"));
          dispatch(setInboxSettings(true)); // Show InboxSettings on XL
          dispatch(setChatInfo(true)); // Show ChatInfo on XL (default open)
        });
      }
    }
  }, [dispatch, isXs, isSm, isMd, isLg, isXl, screenCategory, selectedContactID, userid, userDetails?._id, accountid, navigate, isTeamMemberChat]);

  // Handle chat view on XS: show chat for customer or team-member chats
  useEffect(() => {
    if (isXs && (selectedContactID || isTeamMemberChat)) {
      setShowChatView(true);
    } else if (isXs && !selectedContactID && !isTeamMemberChat) {
      setShowChatView(false);
    }
  }, [isXs, selectedContactID, isTeamMemberChat]);

  // Handle ChatInfo drawer for smaller screens (xs, sm, md, lg)
  useEffect(() => {
    if ((screenSize === "xs" || screenSize === "sm" || screenSize === "md" || screenSize === "lg") && showChatInfo) {
      setShowChatInfoDrawer(true);
    } else if ((screenSize === "xs" || screenSize === "sm" || screenSize === "md" || screenSize === "lg") && !showChatInfo) {
      setShowChatInfoDrawer(false);
    }
  }, [screenSize, showChatInfo]);

  // Sync drawer state with Redux state when drawer closes
  const handleChatInfoDrawerClose = () => {
    setShowChatInfoDrawer(false);
    if (screenSize === "xs" || screenSize === "sm" || screenSize === "md" || screenSize === "lg") {
      dispatch(setChatInfo(false));
    }
  };

  // Live-store check (not render-time values): is a chat open/fetch underway right now? `forContactId`
  // limits it to that contact; omitted, any selection / expected contact / in-flight fetch counts.
  // Effects below run in the same commit as MessageCard's URL-sync effect, whose selectContact dispatches
  // land in the store before these effects run but after the render they close over.
  const openingSelectedNow = (forContactId) => {
    const live = store.getState();
    const liveSelected = live.inboxAll?.selectedContactID;
    const liveExpected = live.inboxMessage?.expectedContactId;
    const liveLoading = !!live.inboxMessage?.loading;
    if (forContactId != null) {
      const id = String(forContactId);
      return (
        (liveExpected != null && String(liveExpected) === id) ||
        (liveLoading && String(liveSelected || "") === id)
      );
    }
    return !!liveSelected || liveExpected != null || liveLoading;
  };

  // Sync /chat/:id URL when a contact is selected; clear selection if inbox no longer contains it after a load.
  // Message/contact loading is handled by useContactSelection / ChatBody — do not dispatch fetches here (avoids
  // re-running on every fetchInbox refresh and duplicating those thunks).
  useEffect(() => {
    if (!selectedContactID) {
      return;
    }

    const hasLoadedInboxAtLeastOnce = hasInboxFetchCompletedRef.current;
    // contactExistsInInbox only looks at the already-loaded inbox buckets — Closed/Blocked
    // are lazy-loaded on first tab click (fetchClosedTabInbox) and stay [] until then, so a
    // closed chat opened before that (deep link, refresh, notification) reads as "not found"
    // here even though it's real. Messages having actually loaded for this exact contact is
    // an equally strong "this contact exists" signal, so treat either as sufficient — avoids
    // yanking the user back to "No conversation selected" right after a successful fetch.
    const messagesMatchSelected =
      !!messages &&
      String(messages.contactID || messages.contactId || "") === String(selectedContactID);
    const selectedStillExists = !hasLoadedInboxAtLeastOnce || contactExistsInInbox || messagesMatchSelected;

    // A fetch for this exact contact is in flight (or just about to be, since
    // selectContactUnified sets expectedContactId synchronously before the thunk's
    // pending action lands) — same race the URL-normalize effect below guards
    // against. Without this, a contact from a lazy-loaded bucket (Closed/Blocked)
    // gets its selectedContactID wiped the instant it's clicked: contactExistsInInbox
    // is still false (bucket hasn't loaded) and messages hasn't arrived yet either,
    // so selectedStillExists reads false before the fetch (visible in Network) has
    // any chance to land and satisfy messagesMatchSelected.
    const fetchInFlightForSelected =
      inboxMessageLoading || String(inboxExpectedContactId || "") === String(selectedContactID);

    if (
      hasLoadedInboxAtLeastOnce &&
      !inboxRecentLoading &&
      !inboxRecentError &&
      !selectedStillExists &&
      !fetchInFlightForSelected &&
      !openingSelectedNow(selectedContactID)
    ) {
      console.warn("[DL-TRACE] Inbox.jsx existence-check clear", { selectedContactID, contactExistsInInbox, messagesMatchSelected, inboxMessageLoading, inboxExpectedContactId });
      dispatch(setselectedContactID(null));
      const effectiveUserid = userid || userDetails?._id;
      if (accountid && effectiveUserid) {
        navigate(`/home/${accountid}/inbox/${effectiveUserid}`, { replace: true });
      }
      return;
    }

    const isChatWithMembersTab = selectedInboxTab === "Chat with Members";
    const effectiveUserid = userid || userDetails?._id;
    if (
      effectiveUserid &&
      !isChatWithMembersTab &&
      contactExistsInInbox &&
      typeof window !== "undefined" &&
      !window.location.pathname.includes(`/chat/${selectedContactID}`)
    ) {
      const chatUrl = `/home/${accountid}/inbox/${effectiveUserid}/chat/${selectedContactID}`;
      navigate(chatUrl, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openingSelectedNow only reads the live store
  }, [
    dispatch,
    selectedContactID,
    accountid,
    userid,
    userDetails,
    navigate,
    selectedInboxTab,
    contactExistsInInbox,
    inboxRecentLoading,
    inboxRecentError,
    messages,
    inboxMessageLoading,
    inboxExpectedContactId,
  ]);

  // If nothing is selected but the URL still has /chat/:id (e.g. after tab switch), normalize the route
  useEffect(() => {
    if (selectedContactID) return;
    if (isTeamMemberChat) return;
    if (typeof window === "undefined") return;
    if (!window.location.pathname.includes("/chat/")) return;
    // Opening a chat from URL or switching: selection commits only after load — avoid stripping /chat/:id mid-flight
    if (inboxMessageLoading || inboxExpectedContactId != null) {
      return;
    }
    // Cold store (page refreshed on another page, e.g. Reports, then a contact link clicked): the
    // inbox hasn't finished its first load, so MessageCard hasn't started opening this chat yet
    // (it waits for `initialized`). Nothing is selected or in flight only because it is too early,
    // not because the URL is stale -- stripping /chat/:id here threw the deep link away before it
    // ever started. A real first-load failure also sets `initialized`, so this can't wait forever.
    if (!inboxRecentInitialized) {
      return;
    }
    // This effect runs in the same commit as MessageCard's own URL->selectContact effect (children
    // first), which dispatches the selection/expected-contact into the store -- but the values this
    // closure captured are from the render BEFORE that, so they still read "nothing selected, nothing
    // in flight". Re-read the live store: if the chat is already being opened, leave the URL alone.
    if (openingSelectedNow()) {
      return;
    }
    const effectiveUserid = userid || userDetails?._id;
    if (!accountid || !effectiveUserid) return;
    console.warn("[DL-TRACE] Inbox.jsx no-selection URL normalize", { inboxMessageLoading, inboxExpectedContactId, inboxRecentInitialized });
    navigate(`/home/${accountid}/inbox/${effectiveUserid}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openingSelectedNow only reads the live store
  }, [
    inboxRecentInitialized,
    selectedContactID,
    isTeamMemberChat,
    userid,
    userDetails?._id,
    accountid,
    navigate,
    inboxMessageLoading,
    inboxExpectedContactId,
  ]);

  // Close ChatInfo on XL screens when no contact is selected
  useEffect(() => {
    if (screenSize === "xl" && !selectedContactID && showChatInfo) {
      dispatch(setChatInfo(false));
      chatInfoManuallyClosedRef.current = false; // Reset flag when contact is deselected
    }
  }, [screenSize, selectedContactID, showChatInfo, dispatch]);

  // Track when user manually closes ChatInfo on XL screens
  const prevShowChatInfoRef = useRef(showChatInfo);
  useEffect(() => {
    if (screenSize === "xl" && selectedContactID) {
      // If ChatInfo was open and is now closed, user manually closed it
      if (prevShowChatInfoRef.current === true && showChatInfo === false) {
        chatInfoManuallyClosedRef.current = true;
      }
    }
    prevShowChatInfoRef.current = showChatInfo;
  }, [screenSize, selectedContactID, showChatInfo]);

  // Reset manual close flag and auto-open ChatInfo when a new contact is selected on XL
  useEffect(() => {
    if (screenSize === "xl" && selectedContactID) {
      // Check if this is a new contact selection (different from previous)
      if (prevSelectedContactIDRef.current !== selectedContactID) {
        // New contact selected - reset manual close flag and auto-open ChatInfo
        chatInfoManuallyClosedRef.current = false;
        if (!showChatInfo) {
          dispatch(setChatInfo(true));
        }
      } else if (!showChatInfo && !chatInfoManuallyClosedRef.current) {
        // Same contact but ChatInfo is closed and wasn't manually closed - auto-open
        dispatch(setChatInfo(true));
      }
    }
    prevSelectedContactIDRef.current = selectedContactID;
  }, [screenSize, selectedContactID, showChatInfo, dispatch]);

  useEffect(() => {
    // Refresh message cards once on inbox open — not tied to userDetails so it
    // doesn't re-dispatch a second time once userDetails finishes loading.
    dispatch(fetchInbox());
  }, [dispatch]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const active = document.activeElement;
      // Ignore if typing in an input, textarea, or contenteditable
      if (
        !active ||
        active.tagName === "INPUT" ||
        active.tagName === "TEXTAREA" ||
        active.isContentEditable
      ) {
        return;
      }
      // Some keydown events omit `key` (e.g. certain IME/autofill cases); avoid crashing
      if (typeof e.key !== "string") {
        return;
      }
      const key = e.key.toLowerCase();
      // Shortcuts
      if (e.ctrlKey && e.shiftKey && key === "y") {
        setOpenDialog("close"); // Close conversation
        e.preventDefault();
      } else if (key === "a") {
        setOpenDialog("assign"); // Assign
        e.preventDefault();
      } else if (key === "t") {
        setOpenDialog("template"); // Send template message
        e.preventDefault();
      } else if (key === "n") {
        setOpenDialog("label"); // Add label
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Handle back button for XS
  const handleBackToRecents = () => {
    setShowChatView(false);
    dispatch(setselectedContactID(null)); // Clear selected contact
    dispatch(clearCurrentChat()); // Clear team-member chat on XS back
    navigate(`/home/${accountid}/inbox/${userid}`);
  };

  // Get available height for current screen size
  const availableHeight = getAvailableHeight(screenSize);
  const drawerTopInset =
    screenSize === "xs"
      ? "calc(var(--top-nav-height, 40px) + var(--trial-banner-height, 0px))"
      : "calc(var(--top-nav-height, 50px) + var(--trial-banner-height, 0px) + 10px)";
  const mobileDrawerBottomInset =
    screenSize === "xs" || screenSize === "sm"
      ? "var(--mobile-bottom-nav-height, 56px)"
      : "0px";
  const drawerPaperSx = {
    top: drawerTopInset,
    bottom: mobileDrawerBottomInset,
    height: `calc(100dvh - ${drawerTopInset} - ${mobileDrawerBottomInset})`,
    boxShadow: "none",
  };
  const drawerBackdropSx = {
    top: drawerTopInset,
    bottom: mobileDrawerBottomInset,
  };

  // Persistent-panel layout for SM/MD/LG/XL (see the render block below for why this
  // must stay a single Grid child per component instead of per-breakpoint duplicates).
  // showInboxSettings now also gates XL (2026-08-18, was force-true there — the
  // InboxSettings panel-heading icon had no way to collapse it on the most common
  // desktop breakpoint at all). InboxAll's own width is untouched either way
  // ("keeping the [list] panel as it is") — the freed column goes entirely to
  // ChatBox below ("making the [chat] panel big").
  const showInboxSettingsPanel =
    (screenSize === "xl" || screenSize === "lg") && showInboxSettings;
  // "Valid to mount at all" (a real contact is selected, not a team-member
  // chat) — same safety condition ChatInfo always mounted under, now gating
  // the slim icon rail that replaces its XL entry point instead (Rahim,
  // 2026-08-21: "detach the current detail panel and introduce a new column
  // with new icons"). ChatInfo.jsx itself is untouched — still mounts exactly
  // this way from the SM/MD Drawer below; only the XL/LG inline slot changed.
  // The `showChatInfo` open/closed toggle this used to feed into a Collapse
  // is retired here too — the rail is persistent, not something to show/hide.
  // Extended to LG (2026-08-27, "proper professional responsiveness for
  // small screen" — a real laptop viewport lands in LG, 1200-1535px, not
  // just XL) — LG already used the same persistent-panel pattern for
  // InboxSettings, so this is the natural next breakpoint, not a new one.
  // The old LG Drawer path is retired in the same change below (both the
  // Drawer's own screenSize check and ChatboxNavbar's "i" icon) so LG never
  // has ChatInfo.jsx mounted twice at once.
  // Requires the SELECTED contact's own data to have actually arrived, not
  // just a contactID being set — selectedContactID flips the instant a row
  // is clicked (or a chat URL is loaded directly), well before `messages`
  // (the fetch result) catches up. Without this, the rail + the spanning
  // ChatboxNavbar popped in immediately while ChatBody/TextBox below them
  // were still showing loading state (Rahim, 2026-08-29: "the chat is not
  // even loaded but the rail is there").
  const selectedContactDataReady =
    !!messages?.contactName && messages?.contactID === selectedContactID;
  const canShowChatInfoPanel =
    (screenSize === "xl" || screenSize === "lg") &&
    !!selectedContactID &&
    !isTeamMemberChat &&
    selectedContactDataReady;
  // As a % of the row's width, not a Grid `size` int anymore (2026-08-18) — see the
  // render block below for why: MUI Grid2's CSS-Grid-based columns can't smoothly
  // animate a span-count change (browsers don't tween discrete grid-column spans),
  // which is why the InboxSettings collapse looked like an instant door-slam
  // instead of sliding. Switched that whole row to plain flexbox + MUI `Collapse`
  // (orientation="horizontal") — the same primitive already proven smooth for the
  // Lifecycle/Team Members/Teams sections inside InboxSettings.jsx itself, just
  // applied horizontally here. ChatBox no longer needs its own explicit size at
  // all: it's a plain `flex:1` that fills whatever space its Collapse siblings
  // free up, animating automatically in lockstep with their transition — no
  // separate width math or transition needed on it.
  const inboxAllSize =
    screenSize === "sm" || screenSize === "md"
      ? 3.5
      : screenSize === "lg"
      ? 3
      : screenSize === "xl"
      ? 2.75
      : undefined;

  // Drag-to-resize the conversation-list panel (lg/xl only, matching where it
  // renders as a fixed-width flex sibling of ChatBox — sm/md use the Drawer
  // path instead). Rahim's ask (2026-08-19): "user can stretch it out... to a
  // specific width according to his need," like Front/Intercom's resizable
  // list pane. Clamped to a specific min/max range, not unlimited, and
  // remembered across sessions via localStorage.
  // Floor for the chat-body column itself — see the CHATBOX_MIN_WIDTH usage
  // below for why this exists now (2026-08-27 responsiveness pass).
  const CHATBOX_MIN_WIDTH = 360;
  const INBOX_ALL_MIN_WIDTH = 260;
  // Breakpoint-aware ceiling, not one flat constant (2026-08-27, small-laptop
  // responsiveness pass) — 560 was tuned for XL's 1536px floor. Now that the
  // rail also renders at LG (1200px floor), letting InboxAll claim the same
  // 560px there leaves too little for the chat body once the rail's own
  // drawer opens too. LG gets a tighter ceiling; XL keeps the original room.
  // Lowered again 2026-09-10 (560->480, 420->380, mirrored in ChatInfoRail's
  // own DRAWER_MAX_WIDTH_*) — the two side panels each having their OWN
  // independent cap didn't stop a user from maxing out BOTH at once, which
  // at XL's own 1536px floor left only 1536-64(rail)-560-560=352px for the
  // chat body, already below its own 360px minWidth floor (Rahim, 2026-09-10,
  // screenshot: chat column squeezed to a sliver with both drawers wide).
  // 480+480 leaves 512px at the same floor width -- comfortably above it.
  const INBOX_ALL_MAX_WIDTH_XL = 480;
  const INBOX_ALL_MAX_WIDTH_LG = 380;
  const inboxAllMaxWidth = screenSize === "lg" ? INBOX_ALL_MAX_WIDTH_LG : INBOX_ALL_MAX_WIDTH_XL;
  const INBOX_ALL_WIDTH_STORAGE_KEY = "inboxAllPanelWidthPx";
  // null = "not yet measured" — falls back to the existing percentage-based
  // width (inboxAllSize) until either a stored preference loads or the panel's
  // real rendered width gets measured once, so there's no jump on first mount.
  const [inboxAllWidth, setInboxAllWidth] = useState(() => {
    if (typeof window === "undefined") return null;
    const stored = parseInt(window.localStorage.getItem(INBOX_ALL_WIDTH_STORAGE_KEY), 10);
    return Number.isFinite(stored) && stored >= INBOX_ALL_MIN_WIDTH && stored <= INBOX_ALL_MAX_WIDTH_XL ? stored : null;
  });
  const inboxAllBoxRef = useRef(null);
  const isResizingInboxAllRef = useRef(false);

  useLayoutEffect(() => {
    if (
      inboxAllWidth === null &&
      inboxAllBoxRef.current &&
      (screenSize === "lg" || screenSize === "xl")
    ) {
      const measured = inboxAllBoxRef.current.getBoundingClientRect().width;
      if (measured > 0) setInboxAllWidth(Math.round(measured));
    }
  }, [screenSize, inboxAllWidth]);

  const handleInboxAllResizeStart = useCallback((e) => {
    e.preventDefault();
    isResizingInboxAllRef.current = true;
    const startX = e.clientX;
    const startWidth =
      inboxAllBoxRef.current?.getBoundingClientRect().width || inboxAllWidth || 320;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const handleMouseMove = (moveEvent) => {
      if (!isResizingInboxAllRef.current) return;
      const next = Math.min(
        inboxAllMaxWidth,
        Math.max(INBOX_ALL_MIN_WIDTH, Math.round(startWidth + (moveEvent.clientX - startX)))
      );
      setInboxAllWidth(next);
    };
    const handleMouseUp = () => {
      isResizingInboxAllRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      setInboxAllWidth((current) => {
        if (current != null) {
          window.localStorage.setItem(INBOX_ALL_WIDTH_STORAGE_KEY, String(current));
        }
        return current;
      });
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [inboxAllWidth, inboxAllMaxWidth]);
  // Only truthy at lg/xl once the InboxSettings panel is actually collapsed —
  // this is the "reopen" button (InboxAll.jsx's hamburger icon), so it has no
  // reason to render/do anything while the panel's own heading icon is already
  // visible and handles closing it. (2026-08-18: previously undefined at xl
  // entirely, and this handler used a blind toggle instead of a plain "open" —
  // harmless before since the icon itself was CSS-hidden at lg/xl regardless,
  // but that meant collapsing the panel there had no way to bring it back.)
  const inboxAllMenuClickHandler =
    (screenSize === "lg" || screenSize === "xl") && !showInboxSettingsPanel
      ? () => dispatch(setInboxSettings(true))
      : screenSize === "sm" || screenSize === "md"
      ? () => setShowInboxSettingsDrawer(true)
      : undefined;

  if (ConversationPermissions.View === false) {
    return <AuthView />;
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        paddingBottom: 0,
        marginBottom: 0,
      }}
    >
      <Grid
        container
        sx={{
          width: "100%",
          height: "100%",
          backgroundColor: theme.palette.background.default,
        }}
      >
        {/* Main Content */}
        <Grid
          size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          sx={{
            height: "100%",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Grid container sx={{ 
            height: availableHeight, 
            padding: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 }, 
            margin: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 },
            overflow: "hidden",
            minHeight: 0,
            flex: 1,
          }}>
            {/* XS Breakpoint (0-599px) - Stack Layout */}
            {screenSize === "xs" && (
              <>
                {!showChatView ? (
                  // Show Recents List
                  <Grid 
                    size={12} 
                    sx={{ 
                      height: availableHeight, 
                      position: "relative",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <InboxAll onMenuClick={() => setShowInboxSettingsDrawer(true)} />
                  </Grid>
                ) : (
                  // Show Chat View
                  <Grid 
                    size={12} 
                    sx={{ 
                      height: availableHeight, 
                      position: "relative", 
                      padding: 0, 
                      margin: 0,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Back Button */}
                    <IconButton
                      onClick={handleBackToRecents}
                      sx={{
                        position: "absolute",
                        top: { xs: "17px", sm: 8, md: 8, lg: 8, xl: 8 },
                        left: { xs: 5, sm: 8, md: 8, lg: 8, xl: 8 },
                        zIndex: 1300,
                        backgroundColor: { xs: theme.palette.divider, sm: theme.palette.background.paper, md: theme.palette.background.paper, lg: theme.palette.background.paper, xl: theme.palette.background.paper },
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                        padding: { xs: "5px 5px", sm: "8px", md: "8px", lg: "8px", xl: "8px" },
                        borderRadius: { xs: "30px", sm: "50%", md: "50%", lg: "50%", xl: "50%" },
                        height: { xs: "auto", sm: "auto", md: "auto", lg: "auto", xl: "auto" },
                        minHeight: { xs: "20px", sm: "auto", md: "auto", lg: "auto", xl: "auto" },
                        "& .MuiSvgIcon-root": {
                          fontSize: { xs: "0.725rem", sm: "24px", md: "24px", lg: "24px", xl: "24px" },
                        },
                      }}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                    <ChatBox openDialog={openDialog} setOpenDialog={setOpenDialog} />
                </Grid>
                )}

                {/* InboxSettings Drawer for XS */}
                <Drawer
                  anchor="left"
                  open={showInboxSettingsDrawer}
                  onClose={() => setShowInboxSettingsDrawer(false)}
                  ModalProps={{
                    BackdropProps: {
                      sx: drawerBackdropSx,
                    },
                  }}
                  PaperProps={{
                    sx: {
                      width: { xs: "70vw", sm: "70vw", md: "100%" },
                      maxWidth: { xs: "280px", sm: "300px", md: "320px" },
                      ...drawerPaperSx,
                    },
                  }}
                >
                  <Box sx={{ height: "100%", overflow: "auto" }}>
                    <InboxSettings 
                      isInsideDrawer={true} 
                      onClose={() => setShowInboxSettingsDrawer(false)}
                    />
                  </Box>
                </Drawer>

                {/* ChatInfo Drawer for XS - Hidden for team member chats */}
                {!isTeamMemberChat && (
                  <Drawer
                    anchor="right"
                    open={showChatInfoDrawer}
                    onClose={handleChatInfoDrawerClose}
                    ModalProps={{
                      BackdropProps: {
                        sx: drawerBackdropSx,
                      },
                    }}
                    PaperProps={{
                      sx: {
                        width: "100%",
                        maxWidth: "320px",
                        ...drawerPaperSx,
                      },
                    }}
                  >
                    {isGroup ? (
                      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                        <GroupInfoDrawer
                          groupId={selectedContactID}
                          onClose={handleChatInfoDrawerClose}
                          showCloseButton={true}
                        />
                      </Box>
                    ) : (
                      <Box sx={{ height: "100%", overflow: "auto" }}>
                        <ChatInfo
                          openLabelDialog={openDialog === 'label'}
                          setOpenDialog={setOpenDialog}
                          onClose={handleChatInfoDrawerClose}
                          showCloseButton={true}
                        />
                      </Box>
                    )}
                  </Drawer>
                )}
              </>
            )}

            {/* SM/MD/LG/XL - single persistent layout, sized by screenSize + panel toggles.
                IMPORTANT: InboxAll/ChatBox render as ONE element each across all four
                breakpoints (and across showInboxSettings/showChatInfo toggles) so React never
                unmounts/remounts them from a resize or panel toggle. Only their width and the
                optional InboxSettings/ChatInfo panels change. Remounting ChatBody mid-fetch was
                the root cause of the stuck-loading-skeleton bug (a later response gets dropped
                by the expectedContactId guard in InboxMessageSlice once a remount changes it). */}
            {screenSize !== "xs" && (
              <>
                {/* Plain Box with an explicit width:"100%", not a nested Grid2 `size={12}`
                    item — that didn't reliably span the full row here (left most of the
                    row empty, squeezed everything else into a sliver on the first try).
                    Root cause found: Grid2 IS flexbox under the hood (confirmed in its own
                    source, `display:"flex"` on the container), but each item's width is a
                    CSS calc() driven by a plain (non-@property, non-animatable) custom
                    property — that's also WHY the earlier `transition:"flex-basis..."`
                    attempt never animated even once InboxSettings was kept always-mounted:
                    a `transition` can't interpolate through a custom-property switch, the
                    calc() result just jumps. Collapse below sidesteps this entirely by
                    measuring + animating real pixel widths via JS, not CSS custom
                    properties — same reason it's the only thing that actually looked
                    smooth for Lifecycle/Team Members/Teams in InboxSettings.jsx too. */}
                <Box sx={{ width: "100%", height: availableHeight, display: "flex", overflow: "hidden" }}>
                  {/* Always mounted at lg/xl (never conditionally unmounted like before,
                      2026-08-18) so Collapse can animate it shut instead of it just
                      vanishing/appearing instantly. Doesn't apply at sm/md: those never
                      render this branch at all (showInboxSettingsPanel is always false
                      there), InboxSettings comes from the Drawer further down instead —
                      mounting it here too would double-mount it (duplicate socket/data
                      effects). Same Collapse primitive already proven smooth for the
                      Lifecycle/Team Members/Teams sections inside InboxSettings.jsx
                      itself (orientation="vertical" there, "horizontal" here). */}
                  {(screenSize === "lg" || screenSize === "xl") && (
                    // Fixed px width, not a percentage (2026-08-18, was still broken —
                    // collapsed to a sliver instead of 1/6 the row). Root cause: Collapse
                    // measures the CHILD's own width to animate to, but InboxSettings.jsx's
                    // own root is `width:"100%"` (relies entirely on its parent for size) —
                    // a percentage here is circular (100% of a width Collapse is trying to
                    // derive FROM measuring this same content). A literal px value has
                    // nothing ambiguous to measure, so it isn't.
                    <Collapse in={showInboxSettingsPanel} orientation="horizontal" timeout={300} sx={{ height: availableHeight, flexShrink: 0 }}>
                      <Box sx={{ width: "280px", height: availableHeight, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                        <InboxSettings
                          onClose={
                            screenSize === "lg"
                              ? () => dispatch(setInboxSettings(false))
                              : undefined
                          }
                        />
                      </Box>
                    </Collapse>
                  )}

                  {/* Drag-to-resize (Rahim's ask, 2026-08-19) is now an INVISIBLE overlay
                      strip on top of InboxAll's own existing right border, not a separate
                      sibling element — a separate element physically sat between InboxAll
                      and ChatboxNavbar, splitting their two border lines apart with a real
                      gap between them (the actual root cause of the "lines not joining"
                      bug, not just a rendering quirk). Overlaying the SAME existing border
                      means there is only ever one visual line, period. */}
                  <Box
                    ref={inboxAllBoxRef}
                    sx={{
                      // inboxAllWidth (px, drag-resizable) once measured/loaded from
                      // localStorage; falls back to the original percentage until then
                      // so there's no jump on first mount (see the useLayoutEffect above).
                      // Re-clamped against inboxAllMaxWidth here too (not just at drag
                      // time) — a width stored while on XL (up to 560px) must not carry
                      // straight over if the window is then resized down to LG.
                      width: inboxAllWidth != null ? `${Math.min(inboxAllWidth, inboxAllMaxWidth)}px` : `${(inboxAllSize / 12) * 100}%`,
                      flexShrink: 0,
                      height: availableHeight,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                    }}
                  >
                    <InboxAll onMenuClick={inboxAllMenuClickHandler} />
                    <Box
                      onMouseDown={handleInboxAllResizeStart}
                      sx={{
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        right: 0,
                        // 4px, not 6 — MessageCard.jsx's own "..." row menu button sits
                        // starting at right:6px, so this stays clear of it (confirmed
                        // against that file's own positioning) rather than risking
                        // stealing its hover/click.
                        width: "4px",
                        cursor: "col-resize",
                        zIndex: 2,
                        "&:hover": {
                          // InboxAll's own border-right IS the line — highlighting it in
                          // place on hover, not drawing a second line on top of it.
                          borderRight: (theme) => `2px solid ${theme.palette.primary.main}`,
                        },
                      }}
                    />
                  </Box>

                  {/* flex:1 instead of a computed size (was chatBoxSize) — automatically
                      fills whatever space the Collapse siblings free up/reclaim, animating
                      in lockstep with them for free since flexbox recalculates every frame
                      of their width transition. No separate width math or transition
                      needed here at all.

                      On XL with a real contact selected, ChatboxNavbar now renders HERE
                      (spanning this whole column) instead of inside ChatBox — so it's one
                      real bar stretching over both the chat body AND the rail below, not
                      two separately-colored boxes faking a join (Rahim, 2026-08-21: "use
                      the top navbar to stretch the end of the page"). ChatBox.jsx still
                      renders its own internal navbar exactly as before whenever this one
                      isn't mounted (no contact selected, team-member chat, or any other
                      breakpoint) — see its `hideNavbar` prop. */}
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      height: availableHeight,
                      padding: 0,
                      margin: 0,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {canShowChatInfoPanel && (
                      <ChatboxNavbar
                        openDialog={openDialog}
                        setOpenDialog={setOpenDialog}
                        isTeamMemberChat={isTeamMemberChat || isChatWithMembersTab}
                        otherMember={currentTeamMemberChat?.otherMember}
                      />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "row", overflow: "hidden" }}>
                      {/* CHATBOX_MIN_WIDTH floor (2026-08-27, small-laptop responsiveness
                          pass) — this was bare minWidth:0 before, so InboxAll + InboxSettings
                          + the rail's drawer (all flexShrink:0) could squeeze the chat body
                          arbitrarily thin with no floor at all. Doesn't fully prevent overlap
                          in the most extreme combination (every drag maxed on LG's smaller
                          floor), but stops the common case and keeps message bubbles usable. */}
                      <Box sx={{ flex: 1, minWidth: CHATBOX_MIN_WIDTH, height: "100%", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                        <ChatBox openDialog={openDialog} setOpenDialog={setOpenDialog} hideNavbar={canShowChatInfoPanel} />
                      </Box>

                      {canShowChatInfoPanel && (
                        // Detached from the full ChatInfo panel (Rahim, 2026-08-21) — a
                        // persistent slim icon rail now sits in this slot instead of the
                        // Collapse-animated 280px panel. Inert for now (see ChatInfoRail.jsx);
                        // ChatInfo.jsx itself is unchanged and still mounts as-is from the
                        // SM/MD/LG Drawer below.
                        <ChatInfoRail />
                      )}

                      {/* Mounted but invisible — ChatInfo.jsx isn't just a panel, it also
                          owns the ONLY working live-sync for contactInfo (a window-event
                          listener that patches Redux when assignment/status changes from
                          ANYWHERE — the list's "..." menu, another agent, etc.). Detaching
                          its visible panel above silently detached that listener too,
                          which is why "change assign from the contact menu" stopped
                          reaching the top navbar on XL (Rahim, 2026-08-21 audit). Same
                          fetchContactById call this already makes is deduped against
                          ChatBox's own via the existing single-flight guard, so this
                          doesn't add a second real request.
                          Skipped entirely for a group thread (WhatsApp Groups plan
                          follow-up, 2026-09-26): this listener exists only to live-sync
                          assignment/status, which doesn't exist for groups (decision #2),
                          and ChatInfo.jsx's own fetchContactById effect has no isGroup
                          guard — mounting it here for a group id would fire a 404 against
                          ContactsModel and surface as a spurious contactInfo error, the
                          same bug class already fixed at the other two ChatInfo.jsx call
                          sites in this file. */}
                      {canShowChatInfoPanel && !isGroup && (
                        <Box sx={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}>
                          <ChatInfo openLabelDialog={openDialog === 'label'} setOpenDialog={setOpenDialog} />
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* InboxSettings Drawer - SM/MD only (temporary, hamburger-triggered) */}
                {(screenSize === "sm" || screenSize === "md") && (
                  <Drawer
                    anchor="left"
                    open={showInboxSettingsDrawer}
                    onClose={() => setShowInboxSettingsDrawer(false)}
                    ModalProps={{
                      BackdropProps: {
                        sx: drawerBackdropSx,
                      },
                    }}
                    PaperProps={{
                      sx: {
                        width: { xs: "86vw", sm: "70vw", md: "100%" },
                        maxWidth: { xs: "280px", sm: "300px", md: "320px" },
                        ...drawerPaperSx,
                      },
                    }}
                  >
                    <Box sx={{ height: "100%", overflow: "auto" }}>
                      <InboxSettings
                        isInsideDrawer={true}
                        onClose={() => setShowInboxSettingsDrawer(false)}
                      />
                    </Box>
                  </Drawer>
                )}

                {/* ChatInfo Drawer - SM/MD only (temporary) - hidden for team member chats.
                    LG retired from this path (2026-08-27) — it gets the persistent rail
                    now (see canShowChatInfoPanel above), same as XL. Left in the
                    screenSize check as dead weight would double-mount ChatInfo.jsx (this
                    Drawer's copy + the rail's invisible one) whenever both conditions were
                    briefly true, so it's removed here, not just hidden. */}
                {(screenSize === "sm" || screenSize === "md") && !isTeamMemberChat && (
                  <Drawer
                    anchor="right"
                    open={showChatInfoDrawer}
                    onClose={handleChatInfoDrawerClose}
                    ModalProps={{
                      BackdropProps: {
                        sx: drawerBackdropSx,
                      },
                    }}
                    PaperProps={{
                      sx: {
                        width: "100%",
                        maxWidth: "320px",
                        ...drawerPaperSx,
                      },
                    }}
                  >
                    {isGroup ? (
                      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                        <GroupInfoDrawer
                          groupId={selectedContactID}
                          onClose={handleChatInfoDrawerClose}
                          showCloseButton={true}
                        />
                      </Box>
                    ) : (
                      <Box sx={{ height: "100%", overflow: "auto" }}>
                        <ChatInfo
                          openLabelDialog={openDialog === 'label'}
                          setOpenDialog={setOpenDialog}
                          onClose={handleChatInfoDrawerClose}
                          showCloseButton={true}
                        />
                      </Box>
                    )}
                  </Drawer>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Inbox;